import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SearchableFields } from '~/api/dataFields';
import GroupDocumentsTable from '~/components/GroupDocumentsTable/GroupDocumentsTable';
import { getHandlersKeyFromDocumentGroup } from '~/components/SearchV2/SearchResult/SearchResult.utils';
import { getSearchableFields } from '~/components/SearchV2/utils';
import { useDocumentMultipleLocationsModal } from '~/components/Shared/EcModal/useDocumentMultipleLocationsModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { TableContextType } from '~/enums/TableContextType';
import { TableViewContextType } from '~/enums/TableViewType';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import {
  MODAL_DOCUMENT_GROUP_RELATION,
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
  MODAL_REMOVE_DOCUMENT_IN_GROUP,
} from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { DEFAULT_TABLE_SETTINGS } from '~/utils/table';

import RemoveDocumentInGroupModal from '../RemoveDocumentInGroupModal/RemoveDocumentInGroupModal';
import GroupDocumentsLoadView from './GroupDocumentsLoadView/GroupDocumentsLoadView';
import GroupDocumentsSaveView from './GroupDocumentsSaveView/GroupDocumentsSaveView';
import LinkToParent from './LinkToParent/LinkToParent';

function GroupDocuments({ groupId, numberOfDocs, hasEditPermission = true }) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [setDocumentSelected, renderMultipleLocationModal] =
    useDocumentMultipleLocationsModal(getHandlersKeyFromDocumentGroup);

  const [unlinkDocumentsFromGroup, unlinkDocsResult] =
    api.endpoints.unlinkDocumentsFromGroup.useMutation();
  const { isError: isUnlinkDocsError, isSuccess: isUnlinkDocsSuccess } =
    unlinkDocsResult;

  const tableRef = useRef(null);

  const { data: tableViewSet, isFetching: isFetchingTableViewSet } =
    api.endpoints.getCurrentTableView.useQuery({
      context: TableViewContextType.GROUP,
    });

  const {
    data: filterSections,
    isSuccess: isGetFilterSectionsSuccess,
    isFetching: isFetchingFilterSections,
  } = api.endpoints.getFilterSections.useQuery(undefined);

  const showSaveView = useCallback((groupData) => {
    dispatch(
      slices.documentGroup.actions.setModal({
        modalType: MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
        context: { ...groupData },
      }),
    );
  }, []);

  const showLoadView = useCallback((groupDocuments) => {
    dispatch(
      slices.documentGroup.actions.setModal({
        modalType: MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
        context: { ...groupDocuments },
      }),
    );
  }, []);

  const columnOrder = useSelector(
    slices.documentGroup.selectors.selectColumnOrder,
  );
  const columnWidths = useSelector(
    slices.documentGroup.selectors.selectColumnWidths,
  );
  const tableState = { columnOrder };

  const {
    tableSettings = {
      columnOrder,
      columnWidths,
      pageSize: DEFAULT_TABLE_SETTINGS.pageSize,
      sortBy: DEFAULT_TABLE_SETTINGS.sortBy,
    },
    updateTableSettings,
  } = useTableSettings(TableContextType.GroupDocuments);

  const setColumnOrderAndQueryFields = (updatedColumnOrder) => {
    dispatch(slices.documentGroup.actions.setColumnOrder(updatedColumnOrder));

    updateTableSettings({
      ...tableSettings,
      columnOrder: updatedColumnOrder,
    });

    const isDataFieldId = (columnKey) =>
      !!filterSections?.fields[columnKey] &&
      SearchableFields.DocumentGroup !== columnKey;

    const toDataField = (columnKey) => filterSections?.fields[columnKey];

    const queryFields = updatedColumnOrder
      .filter(isDataFieldId)
      .map(toDataField);

    dispatch(slices.documentGroup.actions.setQueryFields(queryFields));
  };

  const handleLoadView = (id) => {
    dispatch(slices.documentGroup.actions.setCurrentTableView(id));
  };

  const [updateTableView] = api.endpoints.updateTableView.useMutation();

  const updateCurrentTableView = (columnOrder) => {
    const context = TableViewContextType.GROUP;
    const fields = getSearchableFields(columnOrder);

    updateTableView({ context, fields });
  };

  const handleColumnOrderChange = (updatedColumnOrder, actionMeta) => {
    setColumnOrderAndQueryFields(updatedColumnOrder, actionMeta);

    updateCurrentTableView(updatedColumnOrder);
  };

  const showLinkToParent = useCallback((state) => {
    dispatch(
      slices.documentGroup.actions.setModal({
        modalType: MODAL_DOCUMENT_GROUP_RELATION,
        context: { ...state },
      }),
    );
  }, []);

  const unlinkFromParent = useCallback(
    (innerTableState) => {
      const { isAllRowsSelected, selectedRowIds } = innerTableState;
      unlinkDocumentsFromGroup({
        groupId,
        documentGroupIds: isAllRowsSelected ? undefined : selectedRowIds,
        selectAll: isAllRowsSelected,
      });
    },
    [groupId],
  );

  const stickyColumnOrder = ['name'];

  useEffect(() => {
    if (tableViewSet && isGetFilterSectionsSuccess) {
      dispatch(slices.documentGroup.actions.setTableViewSet(tableViewSet));
      const defaultColumnOrder = tableViewSet
        .map((column) => column.field_id)
        .map(String);
      setColumnOrderAndQueryFields(
        [...stickyColumnOrder, ...defaultColumnOrder],
        { action: 'select-option' },
      );
    }
  }, [tableViewSet, isGetFilterSectionsSuccess]);

  const handleSetColumnOrder = () => {
    dispatch(slices.documentGroup.actions.setTableViewSet(tableViewSet));
    const currentColumnOrder = tableViewSet
      .map((column) => column.field_id)
      .map(String);
    const defaultColumnOrder = [...stickyColumnOrder, ...currentColumnOrder];
    dispatch(slices.documentGroup.actions.initColumnOrder(defaultColumnOrder));
    handleColumnOrderChange(defaultColumnOrder, { action: 'select-option' });
  };

  const onLinkToParentComplete = () => {
    handleSetColumnOrder();
    tableRef?.current?.clearSelectedRows();
  };

  useEffect(() => {
    if (isUnlinkDocsSuccess) {
      handleSetColumnOrder();
      tableRef?.current?.clearSelectedRows();
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'GroupDocuments.unlinkSuccess',
          defaultMessage: 'Documents have been detached from parent.',
          description:
            'Success message when documents are unlinked from parent',
        }),
      );
    }
  }, [isUnlinkDocsSuccess, intl]);

  useEffect(() => {
    if (isUnlinkDocsError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'GroupDocuments.unlinkError',
          defaultMessage:
            'It was not possible to detach the documents from the parent, try again later.',
          description: 'Error message when document unlinking fails',
        }),
      );
    }
  }, [isUnlinkDocsError, intl]);

  const isLoading = isFetchingTableViewSet || isFetchingFilterSections;

  const handleUpdate = (state, action) => {
    if (action?.type === 'columnDoneResizing') {
      dispatch(
        slices.documentGroup.actions.patchColumnWidths(
          state.columnResizing.columnWidths,
        ),
      );
    }
  };

  const queryFields = useSelector(
    slices.documentGroup.selectors.selectQueryFields,
  );

  const dataFields = filterSections?.dataFields;

  const handleRemove = useCallback((documentRecordInGroup) => {
    dispatch(
      slices.documentGroup.actions.setModal({
        modalType: MODAL_REMOVE_DOCUMENT_IN_GROUP,
        context: { ...documentRecordInGroup },
      }),
    );
  }, []);

  const rowActions = [
    {
      condition: (row) => !!row.handlers,
      label: intl.formatMessage({
        id: 'GroupDocuments.removeFileAction',
        defaultMessage: 'Remove file from group',
        description: 'Action label for removing file from group',
      }),
      onClick: handleRemove,
    },
  ];

  return (
    <>
      <GroupDocumentsTable
        ref={tableRef}
        enableSelectRows={hasEditPermission}
        columnWidths={columnWidths}
        dataFields={dataFields}
        groupId={groupId}
        isLoading={isLoading}
        onNameClick={setDocumentSelected}
        onLoadView={showLoadView}
        onSaveView={showSaveView}
        onSetColumnOrder={handleColumnOrderChange}
        onShowLinkToParent={showLinkToParent}
        onUnlinkFromParent={unlinkFromParent}
        onUpdate={handleUpdate}
        rowActions={hasEditPermission ? rowActions : []}
        stickyColumnOrder={stickyColumnOrder}
        tableState={tableState}
        numberOfDocs={numberOfDocs}
        queryFields={queryFields}
        enableStickyHeader
      />
      <LinkToParent groupId={groupId} onComplete={onLinkToParentComplete} />
      <RemoveDocumentInGroupModal />
      <GroupDocumentsSaveView />
      <GroupDocumentsLoadView onViewSelected={handleLoadView} />
      {renderMultipleLocationModal()}
    </>
  );
}

export default GroupDocuments;
